import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EmpreendimentoBlocoDropDown } from "../../../incorporacao/domain/models/empreendimento_bloco_dropdown";
import { useLocation } from "react-router";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChecklistServicoEmpreendimentoPagination } from "../hooks/empreendimento_unidade_cadastro/pagination";
import { ItensServicoEmpreendimentoForm, ItensServicoEmpreendimentoSchema } from "../../../incorporacao/presentation/schemas/Empreendimento_cadastro/itens_servico_empreendimento";
import { ChecklistServicoEmpreendimento } from "../../../incorporacao/domain/models/checklist_servico_empreendimento_lista";
import { useChecklistServicoEmpreendimentoFilters } from "../hooks/empreendimento_unidade_cadastro/filters";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { processError } from "../../../../common/utils/base_error";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";

type LoadListaChecklistServicoEmpreendimentoParams = {
    idEmpreendimento?: number | null,
    idsChecklistServico?: number[],
    idsBloco?: number[],
    idsUnidade?: number[],
    idsItemServicoEmpreendimento?: number[]

    currentPage?: number;
    rowsPerPage?: number;
};


export const useEmpreendimentoUnidadeCadastroViewModel = () => {
    const {
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        },
        obras: {
            getChecklistServicosDropDownUseCase,
            getObraHeaderInfoUseCase,
            getObraChecklistServicosDropDownUseCase,
        },
        incorporacao: {
            createItensServicoEmpreendimentoUseCase,
            getEmpreendimentoBlocoDropdownUseCase,
            getAllChecklistServicoEmpreendimentoUseCase,
            updateItensServicoEmpreendimentoByConsultaUseCase,
            deleteItensServicoEmpreendimentoUseCase,
            getIncorporacaoImovelDropDownUseCase,

        },
    } = useDependencyInjection();


    const location = useLocation();
    const pagination = useChecklistServicoEmpreendimentoPagination();
    const debouncedRef = useCreateDebounceHandler(750);

    const filter = useChecklistServicoEmpreendimentoFilters();
    const backOrNavigate = useBackOrNavigate();

    const { pushErrorNotification, pushInfoNotification } = usePageLoader();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    const hookForm = useForm<ItensServicoEmpreendimentoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(ItensServicoEmpreendimentoSchema),
    });

    const salvarPorBloco = hookForm.watch("salvarPorBloco");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedIdEmpreendimento, setSelectedIdEmpreendimento] = useState<number | undefined>(undefined);
    const [selectedIdObra, setSelectedIdObra] = useState<number | undefined>(undefined);
    const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();

    const obrasTopHeaderState = useObrasTopHeaderState();
    const [obrasReportState, loadObrasCommonReportsData] = useObrasCommonReports({ idObra: selectedIdObra });
    const [listBlocoDropDown, setListBlocoDropDown] = useState<EmpreendimentoBlocoDropDown[]>([])
    const [listUnidadeDropDown, setListUnidadeDropDown] = useState<DropDown[]>([])
    const [openCadastro, setOpenCadastro] = useState(true);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);


    // Estado relacionadas aos serviços do empreendimento
    const [listaChecklistServicoEmpreendimento, setListaChecklistServicoEmpreendimento] = useState<ChecklistServicoEmpreendimento[]>([])
    // Valores como vieram do servidor, usados para saber quais registros foram realmente alterados
    const [listaOriginalChecklistServicoEmpreendimento, setListaOriginalChecklistServicoEmpreendimento] = useState<ChecklistServicoEmpreendimento[]>([])

    // O input de valor dispara onChange ao aplicar a máscara na montagem, então a
    // edição é detectada comparando com o valor original e não pelo evento.
    const itensEditados = useMemo(() => {
        const originais = new Map(listaOriginalChecklistServicoEmpreendimento.map(x => [x.idChecklistServicoEmpreendimento, x]));
        const normalizar = (valor: number | null | undefined) => Math.round((valor ?? 0) * 100);

        return listaChecklistServicoEmpreendimento.filter(item => {
            const original = originais.get(item.idChecklistServicoEmpreendimento);
            if (!original) return false;
            return normalizar(item.quantidade) !== normalizar(original.quantidade)
                || normalizar(item.valorUnitario) !== normalizar(original.valorUnitario);
        });
    }, [listaChecklistServicoEmpreendimento, listaOriginalChecklistServicoEmpreendimento]);
    const [checklistServicosDropDown, setChecklistServicosDropDown] = useState<DropDown[]>([])
    // Apenas checklists cadastrados nas áreas da obra (usado no formulário de cadastro)
    const [checklistServicosObraDropDown, setChecklistServicosObraDropDown] = useState<DropDown[]>([])

    //#region CARREGAMENTO

    const loadListaChecklistServicoEmpreendimento = useCallback(async (funcState: LoadListaChecklistServicoEmpreendimentoParams) => {
        try {
            const filteredIdEmpreendimento = funcState.idEmpreendimento ?? filter.idEmpreendimento;
            const idsChecklistServico = funcState.idsChecklistServico ?? filter.idsChecklistServico;
            const idsBloco = funcState.idsBloco ?? filter.idsBlocos;
            const idsUnidade = funcState.idsUnidade ?? filter.idsUnidades;
            const idsItemServicoEmpreendimento = funcState.idsItemServicoEmpreendimento ?? filter.idsItemServicoEmpreendimento;

            const filteredPage = funcState.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState.rowsPerPage ?? pagination.rowsPerPage;

            await getAllChecklistServicoEmpreendimentoUseCase
                .getAllChecklistServicoEmpreendimento(
                    filteredIdEmpreendimento,
                    idsChecklistServico,
                    idsBloco,
                    idsUnidade,
                    idsItemServicoEmpreendimento,

                    filteredPage,
                    filteredRowsPerPage
                );

            pagination.setTotalItems(getAllChecklistServicoEmpreendimentoUseCase.totalItems);
            setListaChecklistServicoEmpreendimento(getAllChecklistServicoEmpreendimentoUseCase.items);
            setListaOriginalChecklistServicoEmpreendimento(getAllChecklistServicoEmpreendimentoUseCase.items);

        } catch (e: unknown) {
            await pushErrorNotification("Erro", "Falha ao buscar itens de serviço do empreendimento!", { isAutoFinish: true });
        }
    }, [
        pagination,
        filter,
        getAllChecklistServicoEmpreendimentoUseCase,
        pushErrorNotification,
    ]);

    const loadBlocoDropDown = useCallback(async (idEmpreendimento: number) => {
        try {
            const idEmpreendimentoState = location.state?.idEmpreendimento as number | 0

            const blocos = await getEmpreendimentoBlocoDropdownUseCase.getEmpreendimentoBlocoDropdown(idEmpreendimento ?? idEmpreendimentoState);
            setListBlocoDropDown([...blocos]);
        } catch (e: unknown) {
            throw new Error("Falha ao buscar centro de custo!");
        }
    }, [
        location.state?.idEmpreendimento,
        getEmpreendimentoBlocoDropdownUseCase,
    ]);

    const loadUnidadesDropDown = useCallback(async (idEmpreendimento: number) => {
        try {
            const unidades = await getIncorporacaoImovelDropDownUseCase.execute(idEmpreendimento);
            setListUnidadeDropDown([...unidades]);
        } catch (e: unknown) {
            throw new Error("Falha ao buscar centro de custo!");
        }
    }, [
        getIncorporacaoImovelDropDownUseCase,
    ]);

    const loadChecklistServicoDropDown = useCallback(async () => {
        try {
            const checklistServicoLista = await getChecklistServicosDropDownUseCase.getChecklistServicosDropDown();
            setChecklistServicosDropDown(
                [...checklistServicoLista.map(x => ({
                    id: x.idChecklistServico,
                    title: x.descricao
                }))]
            );
        } catch (e: unknown) {
            throw new Error("Falha ao buscar checklist de serviço!");
        }
    }, [
        getChecklistServicosDropDownUseCase,
    ]);

    const loadChecklistServicoObraDropDown = useCallback(async (idObra?: number) => {
        const filteredIdObra = idObra ?? selectedIdObra;
        if (!filteredIdObra) {
            setChecklistServicosObraDropDown([]);
            return;
        }

        try {
            const checklistServicoLista = await getObraChecklistServicosDropDownUseCase
                .getObraChecklistServicosDropDown(filteredIdObra, null, undefined, true);
            setChecklistServicosObraDropDown(
                [...checklistServicoLista.map(x => ({
                    id: x.idChecklistServico,
                    title: x.descricao ?? ''
                }))]
            );
        } catch (e: unknown) {
            throw new Error("Falha ao buscar checklists das áreas da obra!");
        }
    }, [
        selectedIdObra,
        getObraChecklistServicosDropDownUseCase,
    ]);

    const loadObraHeaderInfo = useCallback(async (idObra?: number) => {
        const filteredIdObra = idObra ?? selectedIdObra;
        if (!filteredIdObra) {
            setSelectedObraHeaderInfo(undefined);
            return;
        }

        try {
            const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
            setSelectedObraHeaderInfo(response ?? undefined);
        } catch (e: unknown) {
            setSelectedObraHeaderInfo(undefined);
            throw new Error('Falha ao buscar dados da obra!');
        }
    }, [
        selectedIdObra,
        getObraHeaderInfoUseCase,
    ]);
    //#endregion





    //#region  PAGE START / USE EFFECT
    usePageStartBootstrap(async () => {
        try {
            const params = new URLSearchParams(location.search);
            const locationId = params.get('id');
            const idEmpreendimento = locationId && locationId.length > 0 ? Number(locationId) : undefined;
            const locationIdObra = params.get('idObra');
            const idObra = locationIdObra && locationIdObra.length > 0 ? Number(locationIdObra) : undefined;

            setSelectedIdObra(idObra);
            await loadObraHeaderInfo(idObra);
            await loadChecklistServicoObraDropDown(idObra);
            if (idObra) {
                await loadObrasCommonReportsData(idObra);
            }

            if (idEmpreendimento) {
                setSelectedIdEmpreendimento(idEmpreendimento);
                filter.setIdEmpreendimento(idEmpreendimento);

                await loadBlocoDropDown(idEmpreendimento);
                await loadListaChecklistServicoEmpreendimento({ idEmpreendimento });
                await loadChecklistServicoDropDown();
            }
        } catch (e: unknown) {
            throw new Error("Falha ao buscar dados da pagina!");
        }
    });

    useEffect(() => {
        if (salvarPorBloco === 1) {
            hookForm.setValue("salvarPorBloco", 1);
        } else {
            hookForm.setValue("salvarPorBloco", 0);
        }
    }, [salvarPorBloco, hookForm]);


    const handlePageBack = useCallback(() => {
        if (selectedIdObra) {
            backOrNavigate(`/Obras_cadastroarea?id=${selectedIdObra}`);
        } else {
            backOrNavigate('/Obras_lista');
        }
    }, [selectedIdObra, backOrNavigate])

    //#endregion



    //#region ITENS SERVICO EMPREENDIMENTO
    const handleUpdateItensEmpreendimentoByConsulta = useCallback(async () => {
        if (itensEditados.length === 0) {
            pushInfoNotification("AVISO", "Nenhum registro foi alterado.", { isAutoFinish: true });
            return;
        }

        try {
            setSaving(true);
            for (const item of itensEditados) {
                await updateItensServicoEmpreendimentoByConsultaUseCase.execute(
                    item.idChecklistServicoEmpreendimento,
                    item.quantidade ?? 0,
                    item.valorUnitario ?? 0,
                );
                setListaOriginalChecklistServicoEmpreendimento((prevState) => prevState.map(x =>
                    x.idChecklistServicoEmpreendimento === item.idChecklistServicoEmpreendimento ? item : x));
            }
            pushInfoNotification("SUCESSO", "Itens de serviço do empreendimento atualizados com sucesso!", { isAutoFinish: true });
        } catch (e: unknown) {
            pushErrorNotification("ERRO", ["Falha ao atualizar itens de serviço do empreendimento!"], { isAutoFinish: true });
        } finally {
            setSaving(false);
        }
    }, [
        updateItensServicoEmpreendimentoByConsultaUseCase,
        itensEditados,
        pushErrorNotification,
        pushInfoNotification,
    ]);

    const handleDeleteOpenState = useCallback((idChecklistServicoEmpreendimento: number | null, open: boolean) => {
        setOpenDeleteModal(open);
        setSelectedDeleteId(idChecklistServicoEmpreendimento);
    }, []);

    const handleDeleteItensEmpreendimentoByConsulta = useCallback(async () => {
        try {
            if (selectedIdEmpreendimento === null || selectedIdEmpreendimento === undefined) {
                pushErrorNotification("ERRO", ["Selecione um empreendimento."], { isAutoFinish: true });
                return;
            }
            if (selectedDeleteId !== null) {
                await deleteItensServicoEmpreendimentoUseCase.execute(
                    selectedDeleteId,
                    []
                );

                await loadListaChecklistServicoEmpreendimento({ idEmpreendimento: selectedIdEmpreendimento });
                pushInfoNotification("SUCESSO", "Itens de serviço do empreendimento atualizados com sucesso!", { isAutoFinish: true });
                setOpenDeleteModal(false);
                setSelectedDeleteId(null);
            }
        } catch (e: unknown) {
            pushErrorNotification("ERRO", ["Falha ao atualizar itens de serviço do empreendimento!"], { isAutoFinish: true });
        }
    }, [
        deleteItensServicoEmpreendimentoUseCase,
        selectedDeleteId,
        selectedIdEmpreendimento,
        loadListaChecklistServicoEmpreendimento,
        pushErrorNotification,
        pushInfoNotification,
    ]);

    const handleCadastroItensEmpreendimentoSubmit = useCallback(async (form: ItensServicoEmpreendimentoForm) => {
        try {
            const idsChecklistServico = form.idsChecklistServico ?? [];
            if (idsChecklistServico.length === 0) {
                pushErrorNotification("ERRO", ["Selecione pelo menos um checklist de serviço."], { isAutoFinish: true });
                return;
            }
            if (form.salvarPorBloco === 0) {
                if (!form.idsBloco || form.idsBloco.length === 0) {
                    pushErrorNotification("ERRO", ["Selecione pelo menos um bloco."], { isAutoFinish: true });
                    return;
                }
            } else {
                if (!form.idsUnidade || form.idsUnidade.length === 0) {
                    pushErrorNotification("ERRO", ["Selecione pelo menos uma unidade."], { isAutoFinish: true });
                    return;
                }
            }

            if (selectedIdEmpreendimento === null || selectedIdEmpreendimento === undefined) {
                pushErrorNotification("ERRO", ["Selecione um empreendimento."], { isAutoFinish: true });
                return;
            }

            await createItensServicoEmpreendimentoUseCase.createItensServicoEmpreendimento(
                selectedIdEmpreendimento,
                form.idsBloco ?? [],
                form.idsUnidade ?? [],
                idsChecklistServico,
                form.quantidade ?? 0,
                form.valorUnitario ?? 0,
                form.salvarPorBloco,
            );

            filter.setIdsChecklistServico(idsChecklistServico);
            filter.setIdsBlocos(form.idsBloco ?? []);
            filter.setIdsUnidades([]);
            await loadListaChecklistServicoEmpreendimento({
                idEmpreendimento: selectedIdEmpreendimento,
                idsChecklistServico,
                idsBloco: form.idsBloco ?? [],
                idsUnidade: form.idsUnidade ?? []
            });
            pushInfoNotification("SUCESSO", "itens do checklist submetidos com sucesso.", { isAutoFinish: true });
            hookForm.reset();

        } catch (e: unknown) {
            pushErrorNotification("ERRO", processError(e, "Falha ao submeter serviço do empreendimento!"), { isAutoFinish: true });
        }
    }, [
        createItensServicoEmpreendimentoUseCase,
        filter,
        selectedIdEmpreendimento,
        hookForm,
        loadListaChecklistServicoEmpreendimento,
        pushInfoNotification,
        pushErrorNotification,
    ]);

    const handleQuantidadeChange = useCallback((idChecklistServicoEmpreendimento: number, newValue: number | null) => {
        setListaChecklistServicoEmpreendimento((prevState) => {
            return prevState.map((item): ChecklistServicoEmpreendimento => {
                if (item.idChecklistServicoEmpreendimento === idChecklistServicoEmpreendimento) {
                    return {
                        ...item,
                        quantidade: newValue !== null ? Number(newValue) : null,
                    };
                }
                return item;
            });
        });
    }, []);

    const handleValorChange = useCallback((idChecklistServicoEmpreendimento: number, newValue: number | null) => {
        setListaChecklistServicoEmpreendimento((prevState) => {
            return prevState.map((item): ChecklistServicoEmpreendimento => {
                if (item.idChecklistServicoEmpreendimento === idChecklistServicoEmpreendimento) {
                    return {
                        ...item,
                        valorUnitario: newValue !== null ? Number(newValue) : null,
                    };
                }
                return item;
            });
        });
    }, []);

    //#endregion

    const handleOpenCadastro = useCallback((open: boolean) => {
        //  setOpenCadastro(open);
    }, []);

    const handleEditar = useCallback(async (idChecklistServicoEmpreendimento: number, openNewTab: boolean) => {
        if (!selectedIdEmpreendimento) {
            pushErrorNotification("ERRO", ["Empreendimento não selecionado!"], { isAutoFinish: true });
            return;
        }
        const obraParam = selectedIdObra ? `&idObra=${selectedIdObra}` : '';
        if (openNewTab) {
            window.open(`/EmpreendimentoUnidade_itens?id=${selectedIdEmpreendimento}&idChecklistServicoEmpreendimento=${idChecklistServicoEmpreendimento}${obraParam}`, "_blank", "noopener,noreferrer");;
        } else {
            backOrNavigate(`/EmpreendimentoUnidade_itens?id=${selectedIdEmpreendimento}&idChecklistServicoEmpreendimento=${idChecklistServicoEmpreendimento}${obraParam}`, { alternativeRoute: `/EmpreendimentoUnidade_cadastro?id=${selectedIdEmpreendimento}${obraParam}` });
        }
    }, [
        selectedIdEmpreendimento,
        selectedIdObra,
        pushErrorNotification,
        backOrNavigate,
    ]);



    //#region PESQUISA DE ITENS
    const handleIdsBlocoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filter.setIdsBlocos(newValues ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadListaChecklistServicoEmpreendimento({
                        idsBloco: newValues,
                        currentPage: 1,
                    });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar novos itens de serviço do empreendimento!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [
            filter,
            pagination,
            loadListaChecklistServicoEmpreendimento,
            pushErrorNotification
        ]
    );

    const handleIdsChecklistServicoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filter.setIdsChecklistServico(newValues ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadListaChecklistServicoEmpreendimento({
                        idsChecklistServico: newValues,
                        currentPage: 1,

                    });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar novos itens de serviço do empreendimento!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [
            filter,
            pagination,
            loadListaChecklistServicoEmpreendimento,
            pushErrorNotification
        ]
    );

    const handleIdsUnidadeChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filter.setIdsUnidades(newValues ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    await loadListaChecklistServicoEmpreendimento({
                        idsUnidade: newValues,
                        currentPage: 1,
                    });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar novos itens de serviço do empreendimento!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [
            filter,
            pagination,
            loadListaChecklistServicoEmpreendimento,
            pushErrorNotification
        ]
    );

    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filter.setIdsBlocos([]);
            filter.setIdsUnidades([]);
            filter.setIdsChecklistServico([]);
            pagination.setPage(1);
            await loadListaChecklistServicoEmpreendimento({
                idsChecklistServico: [],
                idsBloco: [],
                idsUnidade: [],
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar itens de serviço do empreendimento!'));
        } finally {
            setLoading(false);
        }
    }, [
        filter,
        pagination,
        loadListaChecklistServicoEmpreendimento,
        pushErrorNotification,
    ]);

    const handleClearForm = useCallback(() => {
        hookForm.reset();
    }, [hookForm]);

    //#endregion

    //#region PAGINAÇÃO.
    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            pagination.setPage(1);
            await Promise.all([
                loadObraHeaderInfo(),
                loadChecklistServicoObraDropDown(),
                selectedIdObra ? loadObrasCommonReportsData() : Promise.resolve(),
                loadListaChecklistServicoEmpreendimento({
                    currentPage: 1,
                }),
            ]);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar itens de serviço do empreendimento!'));
        } finally {
            setLoading(false);
        }
    }, [
        pagination,
        selectedIdObra,
        loadObraHeaderInfo,
        loadChecklistServicoObraDropDown,
        loadObrasCommonReportsData,
        loadListaChecklistServicoEmpreendimento,
        pushErrorNotification
    ]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadListaChecklistServicoEmpreendimento({
                currentPage: page + 1,
            });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar itens de serviço do empreendimento!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadListaChecklistServicoEmpreendimento, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadListaChecklistServicoEmpreendimento({
                currentPage: 1,
                rowsPerPage,
            });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar itens de serviço do empreendimento!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadListaChecklistServicoEmpreendimento, pushErrorNotification]);

    //#endregion

    //#region HISTORY
    // const handleOpenChangeHistory = useCallback((
    //     idTabela: number | null,
    //     tipoTabela: LogTipoTabela | null,
    // ) => {
    //     if (idTabela && tipoTabela) {
    //         changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    //     } else {
    //         if (selectedIdEmpreendimento) {
    //             changeHistory.handleOpenChangeHistory(selectedIdEmpreendimento, LogTipoTabela.EMPREENDIMENTO, true);
    //         }
    //     }
    // }, [
    //     selectedIdEmpreendimento,
    //     changeHistory,
    // ]);

    //#endregion

    return {
        saving,
        loading,
        hookForm,
        changeHistory,
        salvarPorBloco,
        listBlocoDropDown,
        listUnidadeDropDown: listUnidadeDropDown ?? [],
        checklistServicosDropDown: checklistServicosDropDown ?? [],
        checklistServicosObraDropDown,
        filters: filter,
        listaChecklistServicoEmpreendimento: listaChecklistServicoEmpreendimento ?? [],
        possuiAlteracaoItens: itensEditados.length > 0,
        openCadastro,
        openDeleteModal,
        pagination,
        selectedObraHeaderInfo: selectedObraHeaderInfo ?? null,
        obrasTopHeaderState,
        obrasReportState,

        handleRowsPerPageChange,
        handlePageChange,
        handleReload,

        handleClearFilters,
        handleClearForm,
        handleIdsChecklistServicoChange,
        handleIdsBlocoChange,
        handleIdsUnidadeChange,
        handleDeleteItensEmpreendimentoByConsulta,
        handleUpdateItensEmpreendimentoByConsulta,
        handleDeleteOpenState,
        handleOpenCadastro,
        handleCadastroItensEmpreendimentoSubmit: hookForm.handleSubmit(handleCadastroItensEmpreendimentoSubmit),
        handleEditar,
        setListBlocoDropDown,
        handlePageBack,
        handleQuantidadeChange,
        handleValorChange,
    };
}



